// Client-side layered merge for the Configuration Store preview. Mirrors the runtime's layering
// (AgentRoutingDocumentCodec.ParseLayered): plain objects merge by key; scalars and arrays from the
// override replace the base. Also produces a pretty-printed merged document annotated with the line
// indices the override contributed (added or changed vs Base) so the preview can highlight them.

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Deep-merge: objects merge by key; everything else (scalars, arrays) is overridden wholesale. */
export function deepMerge(base: unknown, override: unknown): unknown {
  if (isPlainObject(base) && isPlainObject(override)) {
    const out: Record<string, unknown> = { ...base };
    for (const k of Object.keys(override)) {
      out[k] = deepMerge(base[k], override[k]);
    }
    return out;
  }
  return override === undefined ? base : override;
}

interface Emitted {
  text: string;
  hl: boolean;
}

/**
 * Pretty-prints `merged` (2-space indent) and marks every line whose value differs from `base` at the
 * same path — i.e. the override's contribution. Objects present in both recurse (only the changed keys
 * inside are marked); a value that is new or changed is marked wholesale (including its subtree).
 */
export function renderMergedWithHighlights(base: unknown, merged: unknown): { text: string; highlightLines: number[] } {
  const lines: Emitted[] = [];

  const pushMultiline = (raw: string, pad: string, keyPart: string, comma: string, hl: boolean) => {
    // Used for arrays / scalars rendered via JSON.stringify; keeps the current indent on wrapped lines.
    const parts = raw.split('\n');
    parts.forEach((p, i) => {
      const text = i === 0 ? `${pad}${keyPart}${p}` : `${pad}${p}`;
      lines.push({ text: i === parts.length - 1 ? `${text}${comma}` : text, hl });
    });
  };

  const walk = (mVal: unknown, bVal: unknown, indent: number, keyPart: string, isLast: boolean) => {
    const pad = '  '.repeat(indent);
    const comma = isLast ? '' : ',';
    const added = bVal === undefined;

    if (isPlainObject(mVal)) {
      lines.push({ text: `${pad}${keyPart}{`, hl: added });
      const keys = Object.keys(mVal);
      keys.forEach((k, i) => {
        const childB = isPlainObject(bVal) ? (bVal as Record<string, unknown>)[k] : undefined;
        walk(mVal[k], childB, indent + 1, `${JSON.stringify(k)}: `, i === keys.length - 1);
      });
      lines.push({ text: `${pad}}${comma}`, hl: added });
    } else if (Array.isArray(mVal)) {
      const changed = added || JSON.stringify(mVal) !== JSON.stringify(bVal);
      pushMultiline(JSON.stringify(mVal, null, 2), pad, keyPart, comma, changed);
    } else {
      const changed = added || mVal !== bVal;
      lines.push({ text: `${pad}${keyPart}${JSON.stringify(mVal)}`, hl: changed, } as Emitted);
      // append comma to the just-pushed scalar line
      const last = lines[lines.length - 1];
      last.text = `${last.text}${comma}`;
    }
  };

  walk(merged, base, 0, '', true);
  const text = lines.map((l) => l.text).join('\n');
  const highlightLines = lines.map((l, i) => (l.hl ? i : -1)).filter((i) => i >= 0);
  return { text, highlightLines };
}
